import { Toast } from '../../../../components/Common/Toast';
import { callAPI } from '../../../../services/api.service';
import {
  FulfillmentActionNeeded,
  OrderDetailsModel,
  OrderFulfillmentStatusEnum,
} from '../../../../interfaces/order.interface';

export type OrphanBucket = 'awaiting' | 'failed' | 'missing_address';

export interface OrphanRow {
  bucket: OrphanBucket;
  ownerEmail: string | null;
  order: OrderDetailsModel; // handed to the existing recovery card
  orderNumber: string;
  orderType: string;
  customerName: string;
  shippingCountry: string;
  orderStatus: string;
  /** Canonical lifecycle status (stored, or server-derived for un-backfilled docs). */
  fulfillmentStatus: OrderFulfillmentStatusEnum;
  actionNeeded: FulfillmentActionNeeded;
  phase: string;
  waitToShip: boolean;
  hasAddress: boolean;
  reason?: string; // awaiting bucket: why it isn't shipped yet
  bookComplete?: boolean;
  lastFulfillmentError: string;
  printProcessingState: string;
  shipstationOrderId: string;
  parentBulkOrderNumber: string;
  createdAt: string | null;
  ageDays: number | null;
  isLegacy: boolean;
  recommendedAction: string;
}

export interface BundleSummary {
  bundleOrderNumber: string;
  owner: string | null;
  totalBooks: number | null;
  unsyncedCount: number;
}

export interface OrphanedOrdersResult {
  /** The orders-track-from-date cutoff (YYYY-MM-DD) — null when unset or legacy included. */
  trackFrom?: string | null;
  scanned: number;
  truncated: boolean;
  failed: OrphanRow[];
  awaiting: OrphanRow[];
  missingAddress: OrphanRow[];
  bundles: BundleSummary[];
  summary: {
    failed: number;
    failedLegacy: number;
    awaiting: number;
    awaitingTeacherAuth: number;
    awaitingBookNotReady: number;
    missingAddress: number;
  };
}

// Read-only: triages unshipped physical orders into awaiting / failed / missing-address.
export const fetchOrphanedOrders = async (
  opts?: { email?: string; before?: string; includeLegacy?: boolean },
): Promise<OrphanedOrdersResult | null> => {
  try {
    const params = new URLSearchParams();
    if (opts?.email) params.set('email', opts.email);
    if (opts?.before) params.set('before', opts.before);
    if (opts?.includeLegacy) params.set('includeLegacy', 'true');
    const qs = params.toString();
    const response = await callAPI(`admin/orders/orphaned${qs ? `?${qs}` : ''}`);
    if (!response || response?.error) {
      Toast(response?.error || 'Failed to load orders', true);
      return null;
    }
    return response as OrphanedOrdersResult;
  } catch (error) {
    Toast('Failed to load orders', true);
    return null;
  }
};
